// Staff API service — real API calls replacing mock data

import { apiRequest, API_BASE_URL } from './api';
import { getSchoolId } from './auth';

// ── Raw API response types ───────────────────────────────────

export type StaffApiRecord = {
    id: string;
    organization_id: string;
    school_id: string;
    employee_id: string;
    designation_id: string | null;
    department_id: string | null;
    is_teacher: boolean;
    primary_subject_id: string | null;
    joining_date: string;
    metadata: unknown;
    created_at: string;
    updated_at: string;
    user: {
        id: string;
        first_name: string;
        last_name: string | null;
        email: string;
        phone: string | null;
        date_of_birth: string | null;
        gender: string | null;
        address: string | null;
        city: string | null;
        state: string | null;
        district: string | null;
        postal_code: string | null;
        country: string | null;
        is_active: boolean;
        role: { id: number; name: string } | null;
        profile_photo_url: string | null;
        profile_photo_thumb_url: string | null;
    };
    department: { id: string; name: string } | null;
    designation: { id: string; name: string } | null;
};

export type DepartmentOption = {
    id: string;
    name: string;
    is_active: boolean;
};

export type DepartmentRecord = {
    id: string;
    organization_id: string;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
};

export type RoleOption = {
    id: number;
    name: string;
};

export type RoleRecord = {
    id: number;
    name: string;
    permissions: string[];
};

export type StaffListMeta = {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
};

export type StaffListResponse = {
    success: boolean;
    message: string;
    data: StaffApiRecord[];
    meta: StaffListMeta;
};

// ── API Functions ────────────────────────────────────────────

export async function fetchStaffList(params: {
    page?: number;
    perPage?: number;
    departmentId?: string;
    isTeacher?: boolean;
}): Promise<StaffListResponse> {
    const schoolId = getSchoolId();
    const query = new URLSearchParams();
    if (schoolId) query.set('school_id', schoolId);
    if (params.page) query.set('page', String(params.page));
    if (params.perPage) query.set('per_page', String(params.perPage));
    if (params.departmentId) query.set('department_id', params.departmentId);
    if (params.isTeacher !== undefined) query.set('is_teacher', params.isTeacher ? '1' : '0');

    const response = await fetch(`${API_BASE_URL}/api/staff?${query.toString()}`, {
        headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
        },
    });

    const json = await response.json();
    if (!response.ok || !json.success) {
        throw { success: false, message: json.message || 'Failed to fetch staff' };
    }
    return json as StaffListResponse;
}

export async function deleteStaff(id: string): Promise<void> {
    await apiRequest(`/api/staff/${id}`, { method: 'DELETE' });
}

export async function createStaff(data: FormData): Promise<StaffApiRecord> {
    const res = await apiRequest<StaffApiRecord>('/api/staff', {
        method: 'POST',
        body: data,
    });
    return res.data;
}

export async function updateStaff(id: string, data: FormData): Promise<StaffApiRecord> {
    const res = await apiRequest<StaffApiRecord>(`/api/staff/${id}`, {
        method: 'POST',
        body: data,
    });
    return res.data;
}

export async function fetchDepartments(): Promise<DepartmentOption[]> {
    const res = await apiRequest<DepartmentOption[]>('/api/departments?per_page=100');
    return res.data;
}

export async function fetchAllDepartments(): Promise<DepartmentRecord[]> {
    const res = await apiRequest<DepartmentRecord[]>('/api/departments');
    return res.data;
}

export async function createDepartment(payload: { name: string; is_active?: boolean }): Promise<DepartmentRecord> {
    const res = await apiRequest<DepartmentRecord>('/api/departments', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return res.data;
}

export async function updateDepartment(id: string, payload: { name?: string; is_active?: boolean }): Promise<DepartmentRecord> {
    const res = await apiRequest<DepartmentRecord>(`/api/departments/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    return res.data;
}

export async function fetchRoles(): Promise<RoleOption[]> {
    const res = await apiRequest<RoleOption[]>('/api/roles');
    return res.data;
}

export async function fetchAllRoles(): Promise<RoleRecord[]> {
    const res = await apiRequest<RoleRecord[]>('/api/roles');
    return res.data;
}

export type DesignationRecord = {
    id: string;
    organization_id: string;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
};

export async function fetchAllDesignations(): Promise<DesignationRecord[]> {
    const res = await apiRequest<DesignationRecord[]>('/api/designations');
    return res.data;
}

export async function createDesignation(payload: { name: string; is_active?: boolean }): Promise<DesignationRecord> {
    const res = await apiRequest<DesignationRecord>('/api/designations', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return res.data;
}

export async function updateDesignation(id: string, payload: { name?: string; is_active?: boolean }): Promise<DesignationRecord> {
    const res = await apiRequest<DesignationRecord>(`/api/designations/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    return res.data;
}

export type StateOption = { id: number; name: string };
export type DistrictOption = { id: number; name: string };
export type CityOption = { id: number; name: string };

export async function fetchStates(): Promise<StateOption[]> {
    const res = await apiRequest<StateOption[]>('/api/address-lookup/states');
    return res.data;
}

export async function fetchDistrictsByStateId(stateId: number): Promise<DistrictOption[]> {
    const res = await apiRequest<DistrictOption[]>(`/api/address-lookup/states/${stateId}/districts`);
    return res.data;
}

export async function fetchCitiesByStateId(stateId: number): Promise<CityOption[]> {
    const res = await apiRequest<CityOption[]>(`/api/address-lookup/states/${stateId}/cities`);
    return res.data;
}

// ── Mapper ───────────────────────────────────────────────────

/** Maps an API staff record to the flat Staff shape used by the UI. */
export function mapStaffToUi(r: StaffApiRecord) {
    return {
        id: r.id,
        user_id: r.user.id,
        first_name: r.user.first_name,
        last_name: r.user.last_name,
        email: r.user.email,
        phone: r.user.phone,
        date_of_birth: r.user.date_of_birth,
        gender: r.user.gender,
        address: r.user.address,
        state: r.user.state,
        district: r.user.district,
        city: r.user.city,
        postal_code: r.user.postal_code,
        country: r.user.country,
        profile_photo_url: r.user.profile_photo_url,
        employee_id: r.employee_id,
        designation: r.designation?.name ?? r.user.role?.name ?? '',
        designation_id: r.designation_id,
        department: r.department?.name ?? '',
        department_id: r.department_id,
        joining_date: r.joining_date,
        is_teacher: r.is_teacher,
        primary_subject_id: r.primary_subject_id,
        is_active: r.user.is_active,
        created_at: r.created_at,
        updated_at: r.updated_at,
    };
}

export type MappedStaff = ReturnType<typeof mapStaffToUi>;
